/**
 * What a sell is actually signed as — B7.5, `docs/06` §F43 and §F44.
 *
 * ## The venue and the exchange disagree about what a SELL is
 *
 * The venue keeps one book, normalised onto the UP leg, so `SELL UP @ 51` and
 * `BUY DOWN @ 49` are the same order (`docs/04` §7.8). **The settlement
 * contract does not agree.** A SELL tells the CTF Exchange to transfer outcome
 * tokens the seller must already hold; a BUY of the complement tells it both
 * sides are putting up cash, and it mints a fresh UP+DOWN pair against the
 * resulting dollar and hands each side its leg.
 *
 * So an uncovered SELL reverts inside the ConditionalTokens with
 * `SafeMath: subtraction overflow` — an arithmetic error three contracts away
 * from the missing position, *after* the venue has already recorded the fill.
 * The decision (§F43) is that the client rewrites it **before signing**, which
 * is what this module does. The two alternatives were refusing uncovered sells
 * outright, and minting for the user in a separate transaction first, which is
 * too slow inside a sixty-second market.
 *
 * ## The rule
 *
 * For a sell of `S` shares of outcome `X` at `P`¢ (in X's terms), with `H`
 * shares of `X` deliverable:
 *
 * | | Signed as | Why |
 * |---|---|---|
 * | `H ≥ S` | `SELL X @ P`, size `S` | Covered. A real transfer, which is what the exchange expects and what the user means |
 * | `H = 0` | `BUY Y @ (100 − P)`, size `S` | Uncovered. A complementary cross that mints |
 * | `0 < H < S` | **two orders** | One order cannot be half transfer and half mint — the exchange derives its match type from the order itself |
 *
 * The size is unchanged in every case: shares are shares. Only the price flips,
 * and `100 − P` is exact because prices are integer cents 1–99 (invariant 6).
 *
 * ## `H` is a guess, and the two errors are not symmetric
 *
 * The client reads its coverage from the `positions` projection. The venue
 * computes it from the live ledger **minus its own `reservedClose`** — an
 * account holding 100 UP with a resting covered sell of 100 has 100 shares and
 * none of them free to cover a second sell, which is the exact defect
 * `matcher.coveredSplit` exists to prevent. So `coverableShares()` nets off the
 * account's own resting sells, and even then it can be a beat behind.
 *
 * - **Over-estimating `H`** signs a `SELL` for shares the account does not
 *   have. That is the revert this whole module exists to avoid, and it fails on
 *   chain after the venue has recorded the fill.
 * - **Under-estimating `H`** signs a `BUY` of the complement for shares it could
 *   have transferred. It settles fine. The user ends up holding both legs — a
 *   complete set, economically flat, worth exactly $1 and mergeable — but their
 *   collateral is tied up in it and it is not what they asked for.
 *
 * **So round toward the buy.** Every judgement call below goes that way, and
 * that is also what settles the two cases the venue's own split never meets: a
 * covered leg below `min_order_shares` is not a placeable order, and a coverage
 * figure off the `size_step` is not a placeable size. Both give their shares to
 * the buy rather than trying to sell them.
 *
 * ## Where this must not be
 *
 * **Not in `wallet/order.ts`.** That file is the literal translation from
 * request body to signed struct, and `api-server`'s `verifyOrderSignature`
 * rebuilds the struct *from the body* member by member. A rewrite inside it
 * makes the two disagree, which is a signature that verifies locally and is
 * refused `SIGNATURE_INVALID` — §F7's family, which has cost this repository
 * five defects and none was caught by review. The rewrite happens **here**, on
 * the way in, and everything downstream sees an ordinary order.
 *
 * ## What it costs, and who pays it
 *
 * `docs/04` §2.5 promises the API returns an order the way the user entered it.
 * That rule protects a user from the *engine's* normalisation leaking; it does
 * not protect them from this rewrite, because after it the user genuinely did
 * enter a buy — and a `BUY DOWN @ 49` on the wire is indistinguishable from one
 * somebody meant. So the venue echoes a buy back, correctly, on the order row,
 * the fill and the receipt, and **the client does not relabel it**: relabelling
 * needs client-held intent that does not survive a reload or a second tab, and a
 * row that renames itself on refresh is worse than one that never did.
 *
 * The explanation lives on the ticket instead, before the user commits —
 * `describeSell()` is that sentence, and `docs/07` §1 carries the copy.
 *
 * No React, no DOM, no clock (invariant 10).
 */

import type { Outcome } from '@/lib/decimal'

/** `UP` ↔ `DOWN`. The one place the complement is named. */
export function complementOf(outcome: Outcome): Outcome {
  return outcome === 'UP' ? 'DOWN' : 'UP'
}

/**
 * The complement of a price, in the complement's own terms.
 *
 * Exact for every legal price, because prices are integer cents 1–99 — so `P`
 * and `100 − P` are both in range and neither rounds.
 */
export function complementPriceCents(priceCents: number): number {
  return 100 - priceCents
}

/** One order as the user will actually sign it. */
export interface OrderLeg {
  outcome: Outcome
  side: 'BUY' | 'SELL'
  /** Integer cents 1–99, in `outcome`'s own terms. */
  priceCents: number
  /** Micro-shares. */
  sizeShares: bigint
  /**
   * True when this leg is the user's sell expressed as its complement.
   *
   * Read by the ticket to explain what is about to happen. It is deliberately
   * **not** carried onto the wire or into any row: see the header on why the
   * client does not relabel what the venue echoes back.
   */
  rewritten: boolean
}

export interface SellIntent {
  /** The outcome the user wants to be rid of. */
  outcome: Outcome
  /** Micro-shares, already on `size_step`. */
  sizeShares: bigint
  /** The user's price, integer cents 1–99, in `outcome`'s own terms. */
  priceCents: number
  /** Micro-shares of `outcome` free to deliver — see `coverableShares()`. */
  coverableShares: bigint
  /** `config.limits.min_order_size`, in micro-shares. */
  minOrderShares: bigint
  /** `config.limits.size_step`, in micro-shares. */
  sizeStep: bigint
}

/**
 * How many shares of an outcome this account can actually deliver.
 *
 * The position, less every share a live sell order of this account has already
 * promised. That subtraction is `matcher.coveredSplit`'s `reservedClose` on the
 * client's side of the wire, and it exists for the same reason: without it an
 * account holding 100 UP could place two covered sells of 100, both of which
 * this module would sign as `SELL`, and the second one has nothing behind it.
 *
 * A pending order (submitted, no receipt yet) counts too — it is a promise this
 * tab has already made, and it is exactly the beat the projection is behind.
 *
 * Clamped at zero, which is the round-toward-the-buy direction.
 */
export function coverableShares(positionShares: bigint, restingSellShares: bigint): bigint {
  const free = positionShares - restingSellShares
  return free > 0n ? free : 0n
}

/**
 * The one or two orders a sell is signed as.
 *
 * Total: every legal intent produces at least one leg, and the legs' sizes
 * always sum to `sizeShares` exactly — shares are shares, and a rewrite that
 * lost one would be a rewrite that changed the order.
 *
 * The rounding rules, in the order they apply and all in the same direction:
 *
 * 1. Coverage is floored to `size_step`, because a leg off the step is refused
 *    `SIZE_OFF_STEP` and the half-step belongs to the buy.
 * 2. A buy leg below `min_order_shares` takes shares *from* the sell leg until
 *    it reaches the minimum — still under-estimating coverage.
 * 3. A sell leg below `min_order_shares` gives up entirely. Which covers the
 *    order too small to be two legs at all: under `2 × min_order_shares` there
 *    is nothing to split, and the buy takes all of it.
 */
export function splitSell(intent: SellIntent): OrderLeg[] {
  const { outcome, sizeShares, priceCents, minOrderShares, sizeStep } = intent
  const complement = complementOf(outcome)
  const buyLeg = (size: bigint): OrderLeg => ({
    outcome: complement,
    side: 'BUY',
    priceCents: complementPriceCents(priceCents),
    sizeShares: size,
    rewritten: true,
  })
  const sellLeg = (size: bigint): OrderLeg => ({
    outcome,
    side: 'SELL',
    priceCents,
    sizeShares: size,
    rewritten: false,
  })

  if (sizeShares <= 0n) return []

  let covered = intent.coverableShares < sizeShares ? intent.coverableShares : sizeShares
  if (covered < 0n) covered = 0n
  covered = floorToStep(covered, sizeStep)
  let uncovered = sizeShares - covered

  // Rule 2. A buy leg that exists but is unplaceable takes what it needs from
  // the sell leg. Never the other way round: growing the *sell* leg to reach
  // the minimum would be signing a transfer for shares that are not there.
  if (uncovered > 0n && uncovered < minOrderShares) {
    covered = floorToStep(sizeShares - minOrderShares, sizeStep)
    if (covered < 0n) covered = 0n
    uncovered = sizeShares - covered
  }

  // Rule 3. A sell leg that exists but is unplaceable gives up. This is also
  // what catches an order under `2 × min_order_shares`, which cannot be two
  // legs however the shares are divided.
  if (covered > 0n && covered < minOrderShares) {
    covered = 0n
    uncovered = sizeShares
  }

  if (uncovered === 0n) return [sellLeg(covered)]
  if (covered === 0n) return [buyLeg(uncovered)]
  // The close first, deliberately: it locks nothing (`docs/04` §7.7) and cannot
  // be refused for balance, so a partly-covered sell disposes of what the user
  // holds even when the minting half is refused behind it.
  return [sellLeg(covered), buyLeg(uncovered)]
}

/**
 * The sentence the ticket shows before the user commits — `docs/07` §1.
 *
 * This is the whole of what the client owes for the rewrite. `null` when there
 * is nothing to explain, which is a plain covered sell: the user pressed Sell,
 * a sell is what is placed, and a note saying so would be noise.
 */
export function describeSell(legs: readonly OrderLeg[], sharesText: (micro: bigint) => string): string | null {
  const sell = legs.find((leg) => leg.side === 'SELL')
  const buy = legs.find((leg) => leg.side === 'BUY')
  if (!buy) return null

  const asBuy = `${sharesText(buy.sizeShares)} ${buy.outcome} at ${buy.priceCents}¢`
  if (!sell) {
    return `You hold none of these shares, so this is placed as a buy of the other side — ${asBuy}. Same payoff, and it is what you will see in your orders.`
  }
  // **"sell directly", not "you hold"** — the two are not the same number. The
  // sell leg can be smaller than the position when a leg would otherwise land
  // under the minimum size or off the step (see `splitSell`'s rounding rules),
  // and a sentence that told the user what they hold and then placed something
  // else would be the sort of near-miss this whole line exists to prevent.
  return `${sharesText(sell.sizeShares)} of these sell directly. The rest is placed as a buy of the other side — ${asBuy}. Same payoff, and both are what you will see in your orders.`
}

/** The largest multiple of `step` at or below `shares`. `0n` when nothing fits. */
function floorToStep(shares: bigint, step: bigint): bigint {
  if (shares <= 0n) return 0n
  if (step <= 0n) return shares
  return (shares / step) * step
}
